import { motion } from 'framer-motion'
import { FileText, Check, CheckCircle2, MessageSquare } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { Button } from '@/components/ui/Button'
import type { Proposta } from '@/types'
import type { CustomerData } from '@/utils/propostaPublicaUtils'
import { isPublicContractReady } from '@/utils/propostaPublicaUtils'

type Props = {
  proposta: Proposta
  customer: CustomerData
  aceita: boolean
  aceitando: boolean
  onAceitar: () => void
  onSolicitarRevisao: () => void
}

export function PropostaPublicaStep3({
  proposta,
  customer,
  aceita,
  aceitando,
  onAceitar,
  onSolicitarRevisao,
}: Props) {
  return (
    <motion.div
      key="step3"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4 max-w-xl mx-auto"
    >
      {aceita ? (
        <div className="rounded-2xl border border-[var(--brand-primary)]/25 bg-[var(--brand-primary)]/10 p-8 sm:p-10 text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-[var(--brand-primary)]/15 flex items-center justify-center mx-auto">
            <Check size={28} className="text-[var(--brand-primary)]" />
          </div>
          <h2 className="text-xl font-bold text-[var(--brand-primary)]">Proposta Aceita!</h2>
          <p className="text-sm text-[var(--text-muted)] max-w-sm mx-auto">
            Obrigado, <strong className="text-[var(--text-primary)]">{customer.nome}</strong>! Sua proposta foi
            aceita e nossa equipe entrará em contato em breve.
          </p>
          <div className="inline-flex items-center gap-2 text-xs text-[var(--text-muted)] bg-[var(--bg-dark)] border border-[var(--border)] px-4 py-2 rounded-full">
            <CheckCircle2 size={14} className="text-[var(--brand-primary)]" />
            Enviaremos a confirmação para {customer.email}
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 sm:p-6 space-y-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--brand-primary-muted)] flex items-center justify-center">
                <FileText size={18} className="text-[var(--brand-primary)]" />
              </div>
              <div>
                <h2 className="text-base font-bold text-[var(--text-primary)]">Confirme sua Proposta</h2>
                <p className="text-xs text-[var(--text-muted)]">Revise os dados antes de aceitar</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 p-3 rounded-md bg-[var(--bg-dark)] border border-[var(--border)] text-xs">
              <div className="space-y-1">
                <p className="text-[var(--text-muted)]">Assinante</p>
                <p className="text-[var(--text-primary)] font-medium">{customer.nome}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[var(--text-muted)]">E-mail</p>
                <p className="text-[var(--text-primary)] font-medium">{customer.email}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[var(--text-muted)]">Empresa</p>
                <p className="text-[var(--text-primary)] font-medium">{customer.empresa || '—'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[var(--text-muted)]">CNPJ</p>
                <p className="text-[var(--text-primary)] font-medium">{customer.cnpj || '—'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[var(--text-muted)]">Financeiro</p>
                <p className="text-[var(--text-primary)] font-medium">{customer.responsavelFinanceiroNome || '—'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[var(--text-muted)]">E-mail financeiro</p>
                <p className="text-[var(--text-primary)] font-medium">{customer.emailFinanceiro || '—'}</p>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-[var(--glass-border)]">
              <span className="text-sm text-[var(--text-muted)]">Valor Total</span>
              <span className="text-2xl font-bold text-[var(--brand-primary)]">
                {formatCurrency(proposta.totalFinal)}
              </span>
            </div>
          </div>

          <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 sm:p-6 space-y-3 shadow-sm">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              icon={<CheckCircle2 size={16} />}
              loading={aceitando}
              disabled={!isPublicContractReady(customer)}
              onClick={onAceitar}
            >
              Aceitar Proposta
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="w-full"
              icon={<MessageSquare size={14} />}
              onClick={onSolicitarRevisao}
            >
              Solicitar Revisão
            </Button>
            <p className="text-center text-[11px] text-[var(--text-muted)] mt-2">
              Ao aceitar, você concorda com os termos e condições desta proposta comercial.
            </p>
          </div>
        </>
      )}
    </motion.div>
  )
}
